import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3402';

// Add auth token to requests
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export interface TierInvitation {
  id: string;
  tierId: string;
  creatorProfileId: string;
  invitationCode: string;
  maxUses?: number;
  currentUses: number;
  expiresAt?: Date;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  tier?: {
    id: string;
    name: string;
    price: number;
    currency: string;
  };
  creator?: {
    id: string;
    displayName: string;
  };
}

export interface TierInvitationStats {
  total: number;
  active: number;
  expired: number;
  fullyUsed: number;
  totalUses: number;
}

class TierInvitationService {
  /**
   * Get all invitations for a tier
   */
  async getInvitations(tierId?: string): Promise<TierInvitation[]> {
    const params = tierId ? { tierId } : {};
    const response = await axios.get(`${API_URL}/api/tier-invitations`, {
      params,
      headers: getAuthHeaders(),
    });
    return response.data.invitations;
  }

  /**
   * Get a single invitation by ID
   */
  async getInvitation(invitationId: string): Promise<TierInvitation> {
    const response = await axios.get(`${API_URL}/api/tier-invitations/${invitationId}`, {
      headers: getAuthHeaders(),
    });
    return response.data.invitation;
  }

  /**
   * Get invitation by code
   */
  async getInvitationByCode(invitationCode: string): Promise<TierInvitation | null> {
    const response = await axios.get(`${API_URL}/api/tier-invitations/code/${invitationCode}`, {
      headers: getAuthHeaders(),
    });
    return response.data.invitation;
  }

  /**
   * Create a new invitation
   */
  async createInvitation(data: {
    tierId: string;
    maxUses?: number;
    expiresAt?: Date;
  }): Promise<TierInvitation> {
    const response = await axios.post(
      `${API_URL}/api/tier-invitations`,
      data,
      { headers: getAuthHeaders() }
    );
    return response.data.invitation;
  }

  /**
   * Send invitation email
   */
  async sendInvitation(invitationId: string, recipientEmail: string): Promise<void> {
    await axios.post(
      `${API_URL}/api/tier-invitations/${invitationId}/send`,
      { recipientEmail },
      { headers: getAuthHeaders() }
    );
  }

  /**
   * Deactivate an invitation
   */
  async deactivateInvitation(invitationId: string): Promise<TierInvitation> {
    const response = await axios.put(
      `${API_URL}/api/tier-invitations/${invitationId}/deactivate`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data.invitation;
  }

  /**
   * Delete an invitation
   */
  async deleteInvitation(invitationId: string): Promise<void> {
    await axios.delete(`${API_URL}/api/tier-invitations/${invitationId}`, {
      headers: getAuthHeaders(),
    });
  }

  /**
   * Use an invitation (patron joining via invitation)
   */
  async useInvitation(invitationCode: string): Promise<{ invitation: TierInvitation; tierDetails: any }> {
    const response = await axios.post(
      `${API_URL}/api/tier-invitations/use`,
      { invitationCode },
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  /**
   * Get invitation statistics
   */
  async getStats(): Promise<TierInvitationStats> {
    const response = await axios.get(`${API_URL}/api/tier-invitations/stats`, {
      headers: getAuthHeaders(),
    });
    return response.data.stats;
  }
}

export default new TierInvitationService();
